import type { TFunction } from 'i18next';
import type { UserMessageDto } from '../services/messageService';
import { pickLocalized } from './localizedContent';
import type { AppLocale } from '../i18n';

export interface LocalizedStoredMessage {
  subject: string;
  content: string;
}

const SUBJECT_MATCHERS: Array<{ pattern: RegExp; key: string }> = [
  { pattern: /^Welcome to the app$/i, key: 'subjects.welcome' },
  { pattern: /^Rating saved\s*-\s*(.+)$/i, key: 'subjects.ratingSaved' },
  { pattern: /^Comment Received\s*-\s*(.+)$/i, key: 'subjects.commentReceived' },
  { pattern: /^Comment Approved\s*-\s*(.+)$/i, key: 'subjects.commentApproved' },
  { pattern: /^Comment Rejected\s*-\s*(.+)$/i, key: 'subjects.commentRejected' },
];

/** Prefer stored bilingual fields; fall back to legacy English + client-side pattern localization. */
export function resolveStoredMessage(
  message: Pick<UserMessageDto, 'subject' | 'subjectFa' | 'content' | 'contentFa'>,
  locale: AppLocale,
  t: TFunction,
  isRtl: boolean
): LocalizedStoredMessage {
  const hasStoredFa = Boolean(message.subjectFa?.trim() && message.contentFa?.trim());

  if (locale === 'fa' && hasStoredFa) {
    return {
      subject: message.subjectFa!.trim(),
      content: message.contentFa!.trim(),
    };
  }

  const subject = pickLocalized(message.subject, message.subjectFa, locale);
  const content = pickLocalized(message.content, message.contentFa, locale);

  if (locale === 'fa' && !hasStoredFa) {
    return localizeStoredMessage(subject, content, t, isRtl);
  }

  return { subject, content };
}

/** Localize system-generated inbox subject lines for list cards and modal titles. */
export function localizeStoredSubject(subject: string, t: TFunction, _isRtl = false): string {
  for (const { pattern, key } of SUBJECT_MATCHERS) {
    const match = subject.match(pattern);
    if (match) {
      return t(key, { title: match[1]?.trim() ?? '' });
    }
  }
  return subject;
}

/** Localize system-generated inbox HTML (comment/rating notifications) for display. */
export function localizeStoredMessage(
  subject: string,
  content: string,
  t: TFunction,
  isRtl: boolean
): LocalizedStoredMessage {
  const localizedSubject = localizeStoredSubject(subject, t, isRtl);
  let localizedContent = content;

  const bodyReplacements: Array<{ pattern: RegExp; key: string }> = [
    {
      pattern:
        /Welcome to Persianist! We are excited to have you here\. Start exploring lessons, practice regularly, and enjoy your learning journey\./g,
      key: 'body.welcome',
    },
    {
      pattern: /Thanks for rating this lesson! Your stars are visible to other learners right away\./g,
      key: 'body.ratingSavedLesson',
    },
    {
      pattern: /Thanks for rating this book! Your stars are visible to other learners right away\./g,
      key: 'body.ratingSavedBook',
    },
    {
      pattern: /Thank you for your comment! We have received it and it is pending review\./g,
      key: 'body.commentPending',
    },
    {
      pattern:
        /Your comment will be reviewed by our team\. Once approved, it will be published and visible to other learners\./g,
      key: 'body.commentPendingFooter',
    },
    {
      pattern: /Your comment is now visible to other learners\./g,
      key: 'body.commentApprovedFooter',
    },
    {
      pattern: /If you have any questions, please contact support\./g,
      key: 'body.commentRejectedFooter',
    },
    { pattern: /No rating/g, key: 'body.noRating' },
    { pattern: /No comment text/g, key: 'body.noCommentText' },
  ];

  for (const { pattern, key } of bodyReplacements) {
    localizedContent = localizedContent.replace(pattern, () => t(key));
  }

  localizedContent = localizedContent.replace(
    /Your comment has been <strong style="color: [^"]+;">approved<\/strong> for publication\./gi,
    () => t('body.commentStatusApproved')
  );
  localizedContent = localizedContent.replace(
    /Your comment has been <strong style="color: [^"]+;">rejected<\/strong> for publication\./gi,
    () => t('body.commentStatusRejected')
  );

  const labelReplacements: Array<[string, string]> = [
    ['<strong>Course:</strong>', 'labels.course'],
    ['<strong>Level:</strong>', 'labels.level'],
    ['<strong>Lesson:</strong>', 'labels.lesson'],
    ['<strong>Book:</strong>', 'labels.book'],
    ['<strong>Your Rating:</strong>', 'labels.yourRating'],
    ['<strong>Your Comment:</strong>', 'labels.yourComment'],
  ];

  for (const [en, key] of labelReplacements) {
    localizedContent = localizedContent.split(en).join(`<strong>${t(key)}</strong>`);
  }

  if (isRtl) {
    localizedContent = localizedContent.replace(/border-left:\s*4px solid/gi, 'border-right: 4px solid');
  }

  return { subject: localizedSubject, content: localizedContent };
}
